import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../../../api';

const CopyClassesModal = ({
  isOpen,
  onClose,
  tenantId,
  academicYears = [],
  currentAcademicYearId,
  currentSemesterId,
  currentTermId,
  isSemesterBased: propIsSemesterBased,
  onCopySuccess,
  notify,
  createdBy
}) => {
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  // Selection state
  const [sourceAcademicYearId, setSourceAcademicYearId] = useState('');
  const [targetAcademicYearId, setTargetAcademicYearId] = useState('');
  const [sourceSemesterId, setSourceSemesterId] = useState('');
  const [sourceTermId, setSourceTermId] = useState('');

  // Options state
  const [copyFacultyAssignments, setCopyFacultyAssignments] = useState(false);
  const [copyLabBatches, setCopyLabBatches] = useState(false);
  const [allowPartialCopy, setAllowPartialCopy] = useState(false);

  // Loading and result state
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [copyResult, setCopyResult] = useState(null);
  
  // Error state
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  // Source academic year data
  const sourceAcademicYear = useMemo(() => {
    return academicYears.find(ay => ay._id === sourceAcademicYearId);
  }, [academicYears, sourceAcademicYearId]);

  // Target academic year data
  const targetAcademicYear = useMemo(() => {
    return academicYears.find(ay => ay._id === targetAcademicYearId);
  }, [academicYears, targetAcademicYearId]);

  // Available target academic years
  const availableTargetYears = useMemo(() => {
    if (!sourceAcademicYear) return [];
    
    return academicYears.filter(ay => {
      if (ay._id === sourceAcademicYearId) return false;
      return ay.isSemesterBased === sourceAcademicYear.isSemesterBased;
    });
  }, [academicYears, sourceAcademicYearId, sourceAcademicYear]);

  // Source semesters/terms
  const sourceSemesters = useMemo(() => {
    if (!sourceAcademicYear?.isSemesterBased) return [];
    return sourceAcademicYear.semesters || [];
  }, [sourceAcademicYear]);

  const sourceTerms = useMemo(() => {
    if (!sourceAcademicYear || sourceAcademicYear.isSemesterBased) return [];
    return sourceAcademicYear.terms || [];
  }, [sourceAcademicYear]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('📂 CopyClassesModal opened');
      setCurrentStep(1);
      setSourceAcademicYearId('');
      setTargetAcademicYearId('');
      setSourceSemesterId('');
      setSourceTermId('');
      setCopyFacultyAssignments(false);
      setCopyLabBatches(false);
      setAllowPartialCopy(false);
      setPreviewData(null);
      setCopyResult(null);
      setError(null);
      setErrorDetails(null);
      setShowErrorDetails(false);
    }
  }, [isOpen]);

  // Fetch preview data
  const fetchPreview = useCallback(async () => {
    if (!sourceAcademicYearId || !targetAcademicYearId) return;

    try {
      setPreviewLoading(true);
      setError(null);
      
      console.log('🔍 Fetching preview with:', {
        sourceAcademicYearId,
        targetAcademicYearId,
        sourceSemesterId,
        sourceTermId
      });

      const params = { tenantId };
      
      if (sourceAcademicYear?.isSemesterBased && sourceSemesterId) {
        params.semesterId = sourceSemesterId;
      } else if (!sourceAcademicYear?.isSemesterBased && sourceTermId) {
        params.termId = sourceTermId;
      }

      const response = await api.get(`/api/classManager/classes-for-academic-year/${sourceAcademicYearId}`, { params });

      const sourceClasses = response.data?.classes || response.data || [];
      
      // Check for mapping-driven classes
      const mappingDrivenClasses = sourceClasses.filter(c => c.usedMappingForSubjects);
      
      setPreviewData({
        sourceClasses: Array.isArray(sourceClasses) ? sourceClasses : [],
        sourceAcademicYear: sourceAcademicYear?.name || 'Unknown',
        targetAcademicYear: targetAcademicYear?.name || 'Unknown',
        isSemesterBased: sourceAcademicYear?.isSemesterBased || false,
        mappingDrivenCount: mappingDrivenClasses.length,
        totalClasses: sourceClasses.length
      });

    } catch (err) {
      console.error('❌ Error fetching preview:', err);
      
      // Check if this is a structured error response
      if (err.response?.data) {
        const errorData = err.response.data;
        
        if (errorData.error === 'MISSING_SUBJECT_MAPPINGS') {
          setError({
            type: 'MISSING_MAPPINGS',
            message: errorData.message,
            details: errorData.details,
            missingMappings: errorData.missingMappings
          });
          setErrorDetails(errorData.details);
        } else {
          setError({
            type: 'GENERIC',
            message: errorData.message || 'Failed to fetch preview data'
          });
        }
      } else {
        setError({
          type: 'GENERIC',
          message: err.message || 'Failed to fetch preview data'
        });
      }
      
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [sourceAcademicYearId, targetAcademicYearId, sourceSemesterId, sourceTermId, tenantId, sourceAcademicYear, targetAcademicYear?.name]);

  // Fetch preview when moving to step 3
  useEffect(() => {
    if (currentStep === 3 && sourceAcademicYearId && targetAcademicYearId) {
      fetchPreview();
    }
  }, [currentStep, fetchPreview, sourceAcademicYearId, targetAcademicYearId]);

  // Handle copy operation
  const handleCopy = async () => {
    try {
      setLoading(true);
      setError(null);
      setErrorDetails(null);

      const payload = {
        tenantId,
        sourceAcademicYearId,
        targetAcademicYearId,
        createdBy,
        copyFacultyAssignments,
        copyLabBatches,
        allowPartialCopy
      };

      if (sourceAcademicYear?.isSemesterBased && sourceSemesterId) {
        payload.sourceSemesterId = sourceSemesterId;
      } else if (!sourceAcademicYear?.isSemesterBased && sourceTermId) {
        payload.sourceTermId = sourceTermId;
      }

      console.log('🚀 Copying classes with payload:', payload);

      const response = await api.post('/api/classManager/copy-academic-year', payload);

      console.log('✅ Copy response:', response.data);

      setCopyResult(response.data);
      setCurrentStep(4);

      if (onCopySuccess) {
        onCopySuccess(response.data);
      }

      // Show notification based on result
      const { summary, warnings } = response.data;
      if (summary) {
        if (summary.errors > 0) {
          notify?.(`Copy completed with ${summary.errors} errors`, 'warning');
        } else if (warnings) {
          notify?.(`${summary.copied} classes copied. ${summary.skipped} skipped.`, 'warning');
        } else if (summary.copied > 0) {
          notify?.(`Successfully copied ${summary.copied} classes!`, 'success');
        }
      }

    } catch (err) {
      console.error('❌ Error copying classes:', err);
      
      if (err.response?.data) {
        const errorData = err.response.data;
        
        if (errorData.error === 'MISSING_SUBJECT_MAPPINGS') {
          setError({
            type: 'MISSING_MAPPINGS',
            message: errorData.message,
            details: errorData.details,
            missingMappings: errorData.missingMappings
          });
          setErrorDetails(errorData.details);
        } else if (errorData.error === 'SYSTEM_TYPE_MISMATCH') {
          setError({
            type: 'SYSTEM_MISMATCH',
            message: errorData.message,
            details: errorData.details
          });
        } else if (errorData.error === 'CONTEXT_MISMATCH') {
          setError({
            type: 'CONTEXT_MISMATCH',
            message: errorData.message,
            details: errorData.details
          });
        } else {
          setError({
            type: 'GENERIC',
            message: errorData.message || 'Failed to copy classes'
          });
        }
      } else {
        setError({
          type: 'GENERIC',
          message: err.message || 'Failed to copy classes'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Navigation handlers
  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
      setError(null);
      setErrorDetails(null);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      setError(null);
      setErrorDetails(null);
    }
  };

  const handleSourceYearChange = (e) => {
    setSourceAcademicYearId(e.target.value);
    setSourceSemesterId('');
    setSourceTermId('');
    setTargetAcademicYearId('');
    setPreviewData(null);
    setError(null);
    setErrorDetails(null);
  };

  const handleTargetYearChange = (e) => {
    setTargetAcademicYearId(e.target.value);
    setPreviewData(null);
    setError(null);
    setErrorDetails(null);
  };

  const handleEnablePartialCopy = () => {
    setAllowPartialCopy(true);
    setError(null);
    setErrorDetails(null);
    // Retry the copy
    setTimeout(() => handleCopy(), 100);
  };

  // Validation
  const canProceed = useMemo(() => {
    switch (currentStep) {
      case 1:
        return !!sourceAcademicYearId;
      case 2:
        return !!targetAcademicYearId;
      case 3:
        return previewData && previewData.sourceClasses?.length > 0 && !previewLoading;
      default:
        return true;
    }
  }, [currentStep, sourceAcademicYearId, targetAcademicYearId, previewData, previewLoading]);

  if (!isOpen) return null;

  // ============================================
  // ERROR DISPLAY COMPONENTS
  // ============================================
  
  const MissingMappingsError = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-5">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">Cannot Copy Classes</h3>
              <p className="text-red-100 text-base mt-1">Subject mappings are missing in the target academic year</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Main Message */}
          <div className="mb-6">
            <p className="text-gray-800 text-lg">
              {error?.message || 'Some classes cannot be copied because their subject mappings are missing.'}
            </p>
          </div>

          {/* Affected Classes */}
          {error?.missingMappings && error.missingMappings.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
              <div className="flex items-start space-x-3 mb-3">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-700 font-bold text-lg">!</span>
                </div>
                <h4 className="font-semibold text-amber-800 text-lg">Affected Classes</h4>
              </div>
              
              <div className="space-y-3 max-h-48 overflow-y-auto pr-2">
                {error.missingMappings.map((cls, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-4 shadow-sm border border-amber-100">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900 text-base">{cls.className}</p>
                        <p className="text-sm text-gray-600 mt-1">
                          {cls.programName} • {cls.contextInfo}
                        </p>
                      </div>
                      <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                        Missing Mapping
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* What This Means */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6">
            <div className="flex items-start space-x-3">
              <svg className="w-6 h-6 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="font-semibold text-blue-800 text-lg mb-2">What does this mean?</h4>
                <p className="text-blue-700 text-base leading-relaxed">
                  These classes were created using subject mappings that don't exist in the target academic year. 
                  Without these mappings, the system doesn't know which subjects should be included in these classes.
                </p>
              </div>
            </div>
          </div>

          {/* Solutions */}
          <div className="space-y-4 mb-6">
            <h4 className="font-semibold text-gray-800 text-lg">Here's how to fix this:</h4>
            
            {/* Solution 1 */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-5">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-green-700 font-bold text-lg">1</span>
                </div>
                <div className="flex-1">
                  <p className="text-green-800 text-base font-medium mb-2">Create the missing mappings first</p>
                  <p className="text-green-700 text-sm mb-3">
                    Go to <span className="font-semibold">Program-Subject Mappings</span> and create mappings for:
                  </p>
                  <div className="bg-white rounded-lg p-3 text-sm">
                    {errorDetails?.affectedPrograms?.map((prog, idx) => (
                      <div key={idx} className="flex items-center space-x-2 mb-1">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        <span>{prog}</span>
                      </div>
                    ))}
                    {errorDetails?.affectedContexts?.map((ctx, idx) => (
                      <div key={idx} className="flex items-center space-x-2 text-gray-600">
                        <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                        <span>{ctx}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Solution 2 */}
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-5">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-700 font-bold text-lg">2</span>
                </div>
                <div className="flex-1">
                  <p className="text-purple-800 text-base font-medium mb-2">Enable partial copy</p>
                  <p className="text-purple-700 text-sm mb-4">
                    Copy only the classes that have mappings now, and come back for these later.
                  </p>
                  <button
                    onClick={handleEnablePartialCopy}
                    className="px-6 py-3 bg-purple-600 text-white text-base font-medium rounded-xl hover:bg-purple-700 transition-colors shadow-md hover:shadow-lg"
                  >
                    Enable Partial Copy & Continue
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Technical Details (Collapsible) */}
          <div className="border-t border-gray-200 pt-4">
            <button
              onClick={() => setShowErrorDetails(!showErrorDetails)}
              className="flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              <svg className={`w-5 h-5 mr-2 transform transition-transform ${showErrorDetails ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              {showErrorDetails ? 'Hide technical details' : 'Show technical details'}
            </button>
            
            {showErrorDetails && (
              <div className="mt-4 p-4 bg-gray-50 rounded-xl text-xs font-mono text-gray-600 overflow-auto max-h-40">
                <pre className="whitespace-pre-wrap">
                  {JSON.stringify(error?.missingMappings || errorDetails, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t">
          <button
            onClick={() => {
              setError(null);
              setErrorDetails(null);
            }}
            className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-base font-medium"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              setError(null);
              setErrorDetails(null);
            }}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-base font-medium"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );

  const SystemMismatchError = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
        <div className="flex items-center space-x-4 mb-4">
          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900">System Type Mismatch</h3>
        </div>
        <p className="text-gray-600 text-base mb-6">{error?.message}</p>
        <div className="flex justify-end">
          <button
            onClick={() => setError(null)}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );

  const ContextMismatchError = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
        <div className="flex items-center space-x-4 mb-4">
          <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900">Context Mismatch</h3>
        </div>
        <p className="text-gray-600 text-base mb-6">{error?.message}</p>
        <div className="flex justify-end">
          <button
            onClick={() => setError(null)}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );

  const GenericError = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
        <div className="flex items-center space-x-4 mb-4">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900">Error</h3>
        </div>
        <p className="text-gray-600 text-base mb-6">{error?.message || 'An unexpected error occurred'}</p>
        <div className="flex justify-end">
          <button
            onClick={() => setError(null)}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  // Render appropriate error component
  if (error) {
    if (error.type === 'MISSING_MAPPINGS') return <MissingMappingsError />;
    if (error.type === 'SYSTEM_MISMATCH') return <SystemMismatchError />;
    if (error.type === 'CONTEXT_MISMATCH') return <ContextMismatchError />;
    return <GenericError />;
  }

  // ============================================
  // MAIN MODAL RENDER
  // ============================================
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold">Copy Classes</h2>
                <p className="text-purple-200 text-sm">Copy classes from one academic year to another</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Step Indicator */}
          <div className="mt-4 flex items-center justify-center space-x-2">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === currentStep ? 'bg-white text-purple-600' :
                  step < currentStep ? 'bg-purple-400 text-white' : 'bg-purple-400/30 text-purple-200'
                }`}>
                  {step < currentStep ? '✓' : step}
                </div>
                {step < 4 && (
                  <div className={`w-12 h-1 mx-1 rounded ${
                    step < currentStep ? 'bg-purple-400' : 'bg-purple-400/30'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="mt-2 text-center text-sm text-purple-200">
            {currentStep === 1 && 'Select Source'}
            {currentStep === 2 && 'Select Target'}
            {currentStep === 3 && 'Review & Options'}
            {currentStep === 4 && 'Results'}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Source Selection */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Select Source Academic Year</h3>
              
              <div>
                <label className="block text-sm font-medium mb-2">
                  Source Academic Year <span className="text-red-500">*</span>
                </label>
                <select
                  value={sourceAcademicYearId}
                  onChange={handleSourceYearChange}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select Academic Year</option>
                  {academicYears.map(ay => (
                    <option key={ay._id} value={ay._id}>
                      {ay.name} {ay.label ? `(${ay.label})` : ''} - {ay.isSemesterBased ? '📚 Semester' : '📅 Term'} [{ay.status}]
                    </option>
                  ))}
                </select>
              </div>

              {sourceAcademicYear?.isSemesterBased && sourceSemesters.length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-2">Semester (Optional)</label>
                  <select
                    value={sourceSemesterId}
                    onChange={(e) => setSourceSemesterId(e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg"
                  >
                    <option value="">All Semesters</option>
                    {sourceSemesters.map(sem => (
                      <option key={sem._id} value={sem._id}>
                        {sem.semesterName || `Semester ${sem.semesterNumber}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {sourceAcademicYear && !sourceAcademicYear.isSemesterBased && sourceTerms.length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-2">Term (Optional)</label>
                  <select
                    value={sourceTermId}
                    onChange={(e) => setSourceTermId(e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg"
                  >
                    <option value="">All Terms</option>
                    {sourceTerms.map(term => (
                      <option key={term._id || term.id} value={term._id || term.id}>
                        {term.termLabel || term.name || `Term ${term.termNumber}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Target Selection */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Select Target Academic Year</h3>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <span className="font-medium">Copying from:</span>{' '}
                <span className="text-purple-600 font-semibold">{sourceAcademicYear?.name}</span>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Target Academic Year <span className="text-red-500">*</span>
                </label>
                <select
                  value={targetAcademicYearId}
                  onChange={handleTargetYearChange}
                  className="w-full px-4 py-3 border rounded-lg"
                  disabled={!sourceAcademicYear}
                >
                  <option value="">Select Target Academic Year</option>
                  {availableTargetYears.map(ay => (
                    <option key={ay._id} value={ay._id}>
                      {ay.name} {ay.label ? `(${ay.label})` : ''} - {ay.isSemesterBased ? '📚 Semester' : '📅 Term'} [{ay.status}]
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Step 3: Review & Options */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Review & Options</h3>

              {previewLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading preview...</p>
                </div>
              ) : previewData ? (
                <>
                  {/* Copy Summary */}
                  <div className="bg-purple-50 p-6 rounded-lg">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-semibold">Copy Summary</h4>
                      <span className="px-3 py-1 bg-purple-200 text-purple-800 rounded-full text-sm">
                        {previewData.sourceClasses.length} classes
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-white p-3 rounded border border-purple-200">
                        <div className="text-xs text-gray-500">FROM</div>
                        <div className="font-medium text-purple-700">{previewData.sourceAcademicYear}</div>
                      </div>
                      <div className="bg-white p-3 rounded border border-green-200">
                        <div className="text-xs text-gray-500">TO</div>
                        <div className="font-medium text-green-700">{previewData.targetAcademicYear}</div>
                      </div>
                    </div>

                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Classes using subject mappings:</span> {previewData.mappingDrivenCount}
                    </div>
                  </div>

                  {/* Copy Options */}
                  <div className="bg-white border rounded-lg p-4">
                    <h4 className="font-semibold mb-4">Additional Options</h4>
                    
                    <div className="space-y-3">
                      <label className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          checked={copyFacultyAssignments}
                          onChange={(e) => setCopyFacultyAssignments(e.target.checked)}
                          className="mt-1 w-5 h-5 text-purple-600 rounded"
                        />
                        <div>
                          <span className="font-medium">Copy Faculty Assignments</span>
                          <p className="text-sm text-gray-500">
                            Only copies if same faculty is assigned at subject level
                          </p>
                        </div>
                      </label>

                      <label className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          checked={copyLabBatches}
                          onChange={(e) => setCopyLabBatches(e.target.checked)}
                          className="mt-1 w-5 h-5 text-purple-600 rounded"
                        />
                        <div>
                          <span className="font-medium">Copy Lab Batches</span>
                          <p className="text-sm text-gray-500">
                            Creates empty batches with same configuration
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">No preview data available</div>
              )}
            </div>
          )}

          {/* Step 4: Results */}
          {currentStep === 4 && copyResult && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Copy Results</h3>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">{copyResult.summary?.copied || 0}</div>
                  <div className="text-sm text-green-700">Copied</div>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-amber-600">{copyResult.summary?.skipped || 0}</div>
                  <div className="text-sm text-amber-700">Skipped</div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-600">{copyResult.summary?.errors || 0}</div>
                  <div className="text-sm text-red-700">Errors</div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-600">{copyResult.summary?.totalSource || 0}</div>
                  <div className="text-sm text-blue-700">Total</div>
                </div>
              </div>

              {/* Warnings */}
              {copyResult.warnings && (
                <div className="bg-amber-50 p-4 rounded-lg">
                  <p className="text-amber-800">{copyResult.warnings.message}</p>
                </div>
              )}

              {/* Copied Classes */}
              {copyResult.copiedClasses?.length > 0 && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-medium text-green-800 mb-3">✅ Copied ({copyResult.copiedClasses.length})</h4>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {copyResult.copiedClasses.map((cls, idx) => (
                      <div key={idx} className="bg-white p-2 rounded text-sm">
                        <span className="font-medium">{cls.className}</span>
                        <span className="text-gray-500 ml-2">
                          {cls.subjectsCount} subjects
                          {cls.labBatchCopyResult?.copied && ` • ${cls.labBatchCopyResult.batchCount} batches`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Skipped Classes */}
              {copyResult.skippedClasses?.length > 0 && (
                <div className="bg-amber-50 p-4 rounded-lg">
                  <h4 className="font-medium text-amber-800 mb-3">⏭️ Skipped ({copyResult.skippedClasses.length})</h4>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {copyResult.skippedClasses.map((cls, idx) => (
                      <div key={idx} className="bg-white p-2 rounded text-sm">
                        <div className="font-medium">{cls.className}</div>
                        <div className="text-xs text-amber-600">{cls.reason}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50 flex justify-between">
          <div>
            {currentStep > 1 && currentStep < 4 && (
              <button onClick={handleBack} className="px-4 py-2 text-gray-700 hover:text-gray-900">
                ← Back
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            {currentStep < 4 && (
              <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-100">
                Cancel
              </button>
            )}

            {currentStep < 3 && (
              <button
                onClick={handleNext}
                disabled={!canProceed}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                Next →
              </button>
            )}

            {currentStep === 3 && (
              <button
                onClick={handleCopy}
                disabled={!canProceed || loading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Copying...' : 'Copy Classes'}
              </button>
            )}

            {currentStep === 4 && (
              <button onClick={onClose} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                Close
              </button>
            )}
          </div>
        </div>

        {/* Loading Overlay */}
        {(loading || previewLoading) && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
            <div className="bg-white p-4 rounded-lg shadow-lg flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
              <span>{loading ? 'Copying...' : 'Loading...'}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CopyClassesModal;